// src/business/pages/suppliers/SupplierDirectory.jsx
import { useCallback, useEffect, useState } from "react";
import { Search, Link2, Unlink, Key, ShieldCheck } from "lucide-react";
import Swal from "../../../utils/apparifySwal";
import { suppliersAPI } from "../../services/businessErpService";
import { PageHeader, DataTable, Pagination, StatusBadge, FormModal, BtnPrimary, BtnSecondary, EmptyState } from "../../components/erp";
import Panel from "../../components/Panel";

const COLUMNS = [
  { key: "business_supplier_number", label: "#", sortable: true, className: "font-mono text-xs", render: (r) => r.business_supplier_number || r.business_supplier_id || r.id },
  { key: "name", label: "Company", sortable: true, className: "font-medium" },
  { key: "contact_name", label: "Contact" },
  { key: "contact_email", label: "Email", render: (r) => r.contact_email || "—" },
  { key: "contact_phone", label: "Phone", render: (r) => r.contact_phone || "—" },
  { key: "relationship_status", label: "Status", render: (r) => <StatusBadge status={r.relationship_status || "ACTIVE"} /> },
  { key: "actions", label: "", render: (r) => r._actions },
];

export default function SupplierDirectory() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState("company_name");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");

  // Invite code flow
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [codeSubmitting, setCodeSubmitting] = useState(false);

  // Products drawer
  const [showProducts, setShowProducts] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [linkForm, setLinkForm] = useState({ productId: "", catalogSku: "", isPreferred: false });

  // Edit (reserved for future supplier edit modal)
  // const [editing, setEditing] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await suppliersAPI.list({ page, limit: 20, sort: sortField, dir: sortDir, q: search });
      setData(res?.data || res?.items || []);
      setTotalPages(res?.pagination?.totalPages || res?.meta?.totalPages || 1);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, sortField, sortDir, search]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAddByCode = async () => {
    if (!inviteCode.trim()) return;
    setCodeSubmitting(true);
    try {
      await suppliersAPI.addByCode(inviteCode.trim());
      setShowCodeModal(false);
      setInviteCode("");
      Swal.fire({ icon: "success", title: "Supplier connected", timer: 1500, showConfirmButton: false });
      fetch();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Connection failed", text: e?.message || "Invalid or expired code" });
    } finally {
      setCodeSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "Remove supplier?",
      text: "This will deactivate the supplier relationship.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#e11d48",
    });
    if (!isConfirmed) return;
    try {
      await suppliersAPI.remove(id);
      fetch();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed to remove", text: e?.message || "Something went wrong" });
    }
  };

  const viewProducts = async (supplier) => {
    setShowProducts(supplier);
    setProductsLoading(true);
    try {
      const res = await suppliersAPI.getProducts(supplier.id);
      setProducts(res?.data || []);
    } catch {
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleLinkProduct = async () => {
    try {
      await suppliersAPI.linkProduct(showProducts.id, {
        productId: Number(linkForm.productId),
        catalogSku: linkForm.catalogSku,
        isPreferred: linkForm.isPreferred,
      });
      setLinkForm({ productId: "", catalogSku: "", isPreferred: false });
      viewProducts(showProducts);
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed to link product", text: e?.message || "Something went wrong" });
    }
  };

  const handleUnlinkProduct = async (productId) => {
    try {
      await suppliersAPI.unlinkProduct(showProducts.id, productId);
      viewProducts(showProducts);
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed to unlink", text: e?.message || "Something went wrong" });
    }
  };

  const rows = data.map((r) => ({
    ...r,
    _actions: (
      <div className="flex gap-1">
        <button onClick={() => viewProducts(r)} title="Supplier Products"
          className="rounded-lg bg-neutral-100 p-1.5 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700">
          <Link2 className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => handleDelete(r.id)} title="Delete"
          className="rounded-lg bg-rose-50 p-1.5 text-rose-600 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-400 dark:hover:bg-rose-900">
          <Unlink className="h-3.5 w-3.5" />
        </button>
      </div>
    ),
  }));

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Procurement" title="Supplier Directory" subtitle="Manage your network of suppliers">
        <BtnSecondary onClick={() => setShowCodeModal(true)}>
          <Key className="h-4 w-4" /> Use Invite Code
        </BtnSecondary>
      </PageHeader>

      <Panel hideHeader className="border-l-4 border-l-sky-500 bg-sky-50/50 dark:border-l-sky-400 dark:bg-sky-950/30">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" />
          <div className="text-xs leading-relaxed text-neutral-700 dark:text-neutral-300">
            <strong>Invite-code connections only.</strong> Suppliers register independently
            on the Supplier Portal and generate an invite code. Use the code above
            to establish a verified connection.
          </div>
        </div>
      </Panel>

      <Panel title="All Suppliers" right={
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
          <input type="text" placeholder="Search…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="rounded-lg border border-neutral-200 bg-white py-1.5 pl-8 pr-3 text-xs outline-none focus:ring-2 focus:ring-mnla-accent dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200" />
        </div>
      }>
        <DataTable columns={COLUMNS} data={rows} loading={loading}
          emptyMessage="No suppliers registered yet."
          sortField={sortField} sortDir={sortDir}
          onSort={(f, d) => { setSortField(f); setSortDir(d); setPage(1); }} />
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </Panel>

      {/* Products Drawer */}
      <FormModal open={!!showProducts} onClose={() => setShowProducts(null)} title={`Products — ${showProducts?.name || ""}`} size="lg">
        <div className="space-y-4">
          {/* Link form */}
          <div className="flex items-end gap-3 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-800/50">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Product ID</label>
              <input type="number" value={linkForm.productId} onChange={(e) => setLinkForm((f) => ({ ...f, productId: e.target.value }))}
                className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
                placeholder="Product ID" />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Catalog SKU</label>
              <input type="text" value={linkForm.catalogSku} onChange={(e) => setLinkForm((f) => ({ ...f, catalogSku: e.target.value }))}
                className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
                placeholder="Supplier's SKU" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-400">
              <input type="checkbox" checked={linkForm.isPreferred} onChange={(e) => setLinkForm((f) => ({ ...f, isPreferred: e.target.checked }))} />
              Preferred
            </label>
            <BtnPrimary onClick={handleLinkProduct} disabled={!linkForm.productId}>Link</BtnPrimary>
          </div>

          {/* Product list */}
          {productsLoading ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-10 rounded bg-neutral-200 dark:bg-neutral-800" />)}
            </div>
          ) : products.length === 0 ? (
            <EmptyState title="No products linked" description="Link products to this supplier using the form above." />
          ) : (
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {products.map((p) => (
                <div key={p.product_id || p.id} className="flex items-center justify-between py-2">
                  <div>
                    <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">{p.product_name || `Product #${p.product_id}`}</span>
                    {p.catalog_sku && <span className="ml-2 text-xs text-neutral-500 dark:text-neutral-400">SKU: {p.catalog_sku}</span>}
                    {p.is_preferred ? <StatusBadge status="ACTIVE" label="Preferred" className="ml-2" /> : null}
                  </div>
                  <button onClick={() => handleUnlinkProduct(p.product_id)} className="text-xs text-rose-600 hover:text-rose-700 dark:text-rose-400">
                    Unlink
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </FormModal>

      {/* Invite Code Modal */}
      <FormModal open={showCodeModal} onClose={() => setShowCodeModal(false)} title="Use Supplier Invite Code"
        footer={
          <>
            <BtnSecondary onClick={() => setShowCodeModal(false)}>Cancel</BtnSecondary>
            <BtnPrimary onClick={handleAddByCode} disabled={codeSubmitting || !inviteCode.trim()}>
              {codeSubmitting ? "Connecting…" : "Connect"}
            </BtnPrimary>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Enter an invite code provided by a supplier to instantly connect with them.
          </p>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Invite Code *</label>
            <input type="text" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)}
              placeholder="Paste supplier invite code"
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-mono outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100" />
          </div>
        </div>
      </FormModal>
    </div>
  );
}
